// @ts-nocheck
import type { Country } from "react-phone-number-input/input";

import { useState, useEffect, forwardRef, useCallback } from "react";

import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";

import { getCountryCode } from "./utils";
import { CountryListPopover } from "./list";
import { countries } from "../../assets/data/countries";

import type { PhoneInputProps } from "./types";

// ----------------------------------------------------------------------

const getCountryCallingCode = (countryCode: Country) => {
  const countryData = countries.find((c) => c.code === countryCode);
  return countryData?.phone || "84"; // Default to Vietnam
};

const detectCountryFromPhone = (phoneNumber: string): Country | null => {
  if (!phoneNumber || !phoneNumber.startsWith("+")) {
    return null;
  }

  const numeric = phoneNumber.substring(1);
  const sortedCountries = countries
    .filter((c) => c.phone && c.phone !== "")
    .sort((a, b) => b.phone.length - a.phone.length);

  const matchedCountry = sortedCountries.find((country) => {
    if (numeric.startsWith(country.phone)) {
      const remainingDigits = numeric.substring(country.phone.length);
      return remainingDigits.length >= 6;
    }
    return false;
  });

  if (matchedCountry) {
    return matchedCountry.code;
  }

  try {
    const detectedCountry = getCountryCode(phoneNumber);
    return detectedCountry !== "US" ? detectedCountry : null;
  } catch {
    return null;
  }
};

const extractPhoneNumber = (fullPhone: string, countryCode: string) => {
  if (!fullPhone) return "";

  const cleanPhone = fullPhone.replace(/[\s\-()]/g, "");
  if (cleanPhone.startsWith(`+${countryCode}`)) {
    return cleanPhone.substring(countryCode.length + 1);
  }

  if (cleanPhone.startsWith(countryCode) && !cleanPhone.startsWith("+")) {
    return cleanPhone.substring(countryCode.length);
  }

  if (cleanPhone.startsWith("+")) {
    const detectedCountry = detectCountryFromPhone(fullPhone);
    if (detectedCountry) {
      const detectedCountryCode = getCountryCallingCode(detectedCountry);
      if (cleanPhone.startsWith(`+${detectedCountryCode}`)) {
        return cleanPhone.substring(detectedCountryCode.length + 1);
      }
    }
    const withoutPlus = cleanPhone.substring(1);
    for (let i = 1; i <= 4; i += 1) {
      const possibleCountryCode = withoutPlus.substring(0, i);
      const possiblePhoneNumber = withoutPlus.substring(i);
      const countryExists = countries.find(
        (c) => c.phone === possibleCountryCode,
      );
      if (countryExists && possiblePhoneNumber.length >= 6) {
        return possiblePhoneNumber;
      }
    }

    return withoutPlus;
  }

  return cleanPhone;
};

export const PhoneInput = forwardRef<HTMLDivElement, PhoneInputProps>(
  (
    {
      value,
      onChange,
      placeholder,
      country: inputCountryCode,
      disableSelect,
      ...other
    },
    ref,
  ) => {
    const [selectedCountry, setSelectedCountry] = useState<Country>(() => {
      if (inputCountryCode) return inputCountryCode;
      if (value && value.startsWith("+")) {
        const detectedCountry = detectCountryFromPhone(value);
        if (detectedCountry) {
          return detectedCountry;
        }
      }
      return "VN";
    });

    const [displayValue, setDisplayValue] = useState(() => {
      if (!value) return "";
      let countryForExtraction = selectedCountry;
      if (value.startsWith("+")) {
        const detectedCountry = detectCountryFromPhone(value);
        if (detectedCountry) {
          countryForExtraction = detectedCountry;
        }
      }
      const countryCallingCode = getCountryCallingCode(countryForExtraction);
      return extractPhoneNumber(value, countryCallingCode);
    });

    useEffect(() => {
      if (value !== undefined) {
        let newCountry = selectedCountry;
        if (value && value.startsWith("+") && !inputCountryCode) {
          const detectedCountry = detectCountryFromPhone(value);
          if (detectedCountry && detectedCountry !== selectedCountry) {
            newCountry = detectedCountry;
            setSelectedCountry(detectedCountry);
          }
        }

        const countryCallingCode = getCountryCallingCode(newCountry);
        const newDisplayValue = extractPhoneNumber(value, countryCallingCode);

        setDisplayValue((prevDisplayValue) => {
          if (prevDisplayValue === newDisplayValue) return prevDisplayValue;
          return newDisplayValue;
        });
      }
    }, [value, inputCountryCode, selectedCountry]);

    const handleCountryChange = useCallback(
      (newCountry: Country) => {
        setSelectedCountry((prevCountry) => {
          if (prevCountry === newCountry) return prevCountry;

          if (onChange && displayValue) {
            const newCountryCode = getCountryCallingCode(newCountry);
            const fullPhoneNumber = `+${newCountryCode}${displayValue}`;
            onChange(fullPhoneNumber);
          }

          return newCountry;
        });
      },
      [onChange, displayValue],
    );

    const handleInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        let cleanValue = inputValue.replace(/[^0-9+\-() ]/g, "");

        const countryCallingCode = getCountryCallingCode(selectedCountry);
        if (cleanValue.startsWith(`+${countryCallingCode}`)) {
          cleanValue = cleanValue.substring(countryCallingCode.length + 1);
        } else if (cleanValue.startsWith("+")) {
          cleanValue = cleanValue.replace(/^\+\d+/, "");
        }

        setDisplayValue(cleanValue);

        if (onChange) {
          const fullPhoneNumber = cleanValue
            ? `+${countryCallingCode}${cleanValue}`
            : "";
          onChange(fullPhoneNumber);
        }
      },
      [onChange, selectedCountry],
    );

    return (
      <TextField
        ref={ref}
        value={displayValue}
        onChange={handleInputChange}
        placeholder={placeholder ?? "Enter phone number"}
        inputProps={{
          inputMode: "tel",
          pattern: "[0-9+\\-() ]*",
          autoComplete: "tel",
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ ml: 1 }}>
              <CountryListPopover
                countryCode={selectedCountry}
                disabled={disableSelect ?? false}
                onClickCountry={handleCountryChange}
              />
            </InputAdornment>
          ),
        }}
        {...other}
      />
    );
  },
);
